import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import getSocket from "../services/socket.js";
import { isChatOpen, isPageVisible } from "../services/chat-presence.js";

export default function MainLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
    } else {
      setIsSidebarOpen((prev) => !prev);
    }
  };

  useEffect(() => {
    const socket = getSocket();

    // "Online" should mean the user is actively viewing this tab right now
    // (like an Instagram reel pausing when you swipe away) — not just that
    // the app is open somewhere in the background. Report the real
    // visibility state to the server whenever it changes, and again on
    // every (re)connect so a dropped/restored connection starts accurate.
    const reportVisibility = () => {
      socket.emit("presence:visibility", { visible: document.visibilityState === "visible" });
    };

    // Server asks (with an ack) whether we're really here before deciding
    // between no notification / bell only / bell + email for a new message.
    // No reply within its timeout = not reachable.
    const answerReceiverCheck = (payload, ack) => {
      if (typeof ack !== "function") return;
      ack({ visible: isPageVisible(), chatOpen: isChatOpen(payload?.room) });
    };

    socket.connect();

    socket.on("connect", reportVisibility);
    socket.on("chat:receiver-check", answerReceiverCheck);
    document.addEventListener("visibilitychange", reportVisibility);

    socket.on("user-status-updated", ({ userId, isOnline }) => {
      console.log(`User ${userId} is ${isOnline ? "ONLINE" : "OFFLINE"}`);
    });

    return () => {
      document.removeEventListener("visibilitychange", reportVisibility);
      socket.off("connect", reportVisibility);
      socket.off("chat:receiver-check", answerReceiverCheck);
      socket.disconnect();
    };
  }, []);

  return (
    <div className="relative h-screen flex overflow-hidden bg-background text-text_primary transition-colors duration-300">
      <Sidebar
        isOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <div
        className={`flex flex-col flex-1 w-full h-screen min-h-0 overflow-hidden transition-all duration-300 ${
          isSidebarOpen ? "lg:ml-[280px]" : "lg:ml-20 lg:w-[calc(100%-80px)]"
        }`}
      >
        <Header
          toggleSidebar={toggleSidebar}
          isSidebarOpen={isSidebarOpen}
          isMobile={isMobile}
          mobileOpen={mobileOpen}
        />
        <main className="py-0 px-1.5 flex-1 min-h-0 overflow-hidden">
          <div className="h-full min-h-0">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
